import { useState, useEffect } from 'react';
import { SearchableSelect } from '@features/operations-transmitters/components/shared/SearchableSelect';
import { Input } from '@/components/ui/input';
import { EntityFormDialog } from '@/shared/components/EntityFormDialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useSession } from '@/hooks/useSession';
import {
  useCreateInvoice,
  useCustomerOrganisations,
  useCustomerOrganisation,
  useSubscriptionsByCustomer,
  useOrganisationUsers,
  useQuotesByAccount,
  useProjectsByAccount,
} from '../../hooks';
import { useSalesManagers } from '@/features/accounts/hooks/useSubscriptionManagers';
import { useQuoteByNumber } from '@/features/quotes';
import type { Invoice, PaymentTerms, TaxCategory, Currency, Language } from '../../types';
import { getContractDisplay, getUserName, TAX_CATEGORY_OPTIONS } from '../../types';

interface CreateInvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (invoice: Invoice) => void;
  initialCustomerId?: string;
  initialCustomerName?: string;
  initialCustomerAccountNumber?: string;
}

function getTodayDateString(): string {
  // Use America/Montreal timezone per project convention (CLAUDE.md)
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Montreal',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(new Date());
}

function normalizeForMatch(value: string | undefined): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

export function CreateInvoiceModal({
  open,
  onOpenChange,
  onCreated,
  initialCustomerId,
  initialCustomerName,
  initialCustomerAccountNumber,
}: CreateInvoiceModalProps) {
  const { user } = useSession();
  const createInvoiceMutation = useCreateInvoice();

  // Data queries
  const customersQuery = useCustomerOrganisations();
  const salesManagersQuery = useSalesManagers();

  // Form state
  const [customer, setCustomer] = useState('');
  const [salesManager, setSalesManager] = useState('');
  const [projectName, setProjectName] = useState('');
  const [contract, setContract] = useState('');
  const [contractualManager, setContractualManager] = useState('');
  const [language, setLanguage] = useState<Language>('fr');
  const [invoiceDate, setInvoiceDate] = useState(getTodayDateString());
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>('net-30');
  const [taxCategory, setTaxCategory] = useState<TaxCategory>('GST+QST');
  const [currency, setCurrency] = useState<Currency>('CAD');
  const [quoteNumber, setQuoteNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [description, setDescription] = useState('');
  const [hubspotLink, setHubspotLink] = useState('');
  const [gdriveLink, setGdriveLink] = useState('');

  const customers = customersQuery.data || [];
  // New invoices may only be billed to active accounts; churned/suspended/prospect/onboarding
  // accounts are excluded from the picker (the full list is still used for lookups below).
  const activeCustomers = customers.filter((c) => c.status === 'active');
  const salesManagers = salesManagersQuery.data || [];

  // Dependent queries - enabled when customer is selected
  const selectedCustomerQuery = useCustomerOrganisation(customer);
  const selectedCustomer = selectedCustomerQuery.data || customers.find((c) => c._id === customer);
  const mongoOrgId = selectedCustomer?.mongoOrgId || '';
  const contractsQuery = useSubscriptionsByCustomer(mongoOrgId, customer || undefined);
  const orgUsersQuery = useOrganisationUsers(customer);
  const quotesQuery = useQuotesByAccount(customer);
  const projectsQuery = useProjectsByAccount(customer);
  // Full detail of the selected quote — the account-quotes list doesn't carry the AM.
  const selectedQuoteDetail = useQuoteByNumber(quoteNumber || undefined);
  const contracts = contractsQuery.data || [];
  const orgUsers = orgUsersQuery.data || [];
  const quotes = quotesQuery.data || [];
  const projects = projectsQuery.data || [];

  // Set default sales manager to current user when data loads
  useEffect(() => {
    if (user?._id && !salesManager) {
      setSalesManager(user._id);
    }
  }, [user?._id, salesManager]);

  // When a quote is selected, inherit its Account Manager (the quote's sales_manager,
  // resolved to a Mongo user id — the id-space the AM dropdown matches on). The user
  // can still override afterwards; switching quotes re-applies the new quote's AM.
  useEffect(() => {
    const amId = selectedQuoteDetail.data?.salesManagerId;
    if (amId) setSalesManager(amId);
  }, [selectedQuoteDetail.data]);

  // Reset dependent fields when customer changes
  useEffect(() => {
    setContract('');
    setContractualManager('');
    setQuoteNumber('');
    setProjectNumber('');
  }, [customer]);

  // Prefill customer when opened from account context
  useEffect(() => {
    if (!open || customer || customers.length === 0) return;

    const normalizedAccountNumber = (initialCustomerAccountNumber || '').replace(/^0+/, '');
    const normalizedInitialName = normalizeForMatch(initialCustomerName);
    const customerById = initialCustomerId
      ? customers.find((org) => org._id === initialCustomerId)
      : undefined;
    const customerByOrgNum = normalizedAccountNumber
      ? customers.find((org) => String(org.org_num || '').replace(/^0+/, '') === normalizedAccountNumber)
      : undefined;
    const customerByExactName = normalizedInitialName
      ? customers.find((org) => normalizeForMatch(org.name) === normalizedInitialName)
      : undefined;
    const customerByFuzzyName = normalizedInitialName
      ? customers.find((org) => {
          const normalizedOrgName = normalizeForMatch(org.name);
          return normalizedOrgName.includes(normalizedInitialName)
            || normalizedInitialName.includes(normalizedOrgName);
        })
      : undefined;

    const matchedCustomer =
      customerById
      || customerByOrgNum
      || customerByExactName
      || customerByFuzzyName;
    if (matchedCustomer) {
      setCustomer(matchedCustomer._id);
    }
  }, [
    open,
    customer,
    customers,
    initialCustomerId,
    initialCustomerName,
    initialCustomerAccountNumber,
  ]);

  const resetForm = () => {
    setCustomer('');
    setSalesManager(user?._id || '');
    setProjectName('');
    setContract('');
    setContractualManager('');
    setLanguage('fr');
    setInvoiceDate(getTodayDateString());
    setPaymentTerms('net-30');
    setTaxCategory('GST+QST');
    setCurrency('CAD');
    setQuoteNumber('');
    setPoNumber('');
    setProjectNumber('');
    setDescription('');
    setHubspotLink('');
    setGdriveLink('');
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  const isValid = !!customer && !!mongoOrgId && !!salesManager && !!projectName.trim();

  const handleSubmit = async () => {
    if (!isValid) return;

    try {
      // Resolve PG UUID → MongoDB ObjectId for the contract field (invoices live in MongoDB)
      const resolvedContract = contract
        ? (contracts.find(c => c._id === contract)?.mongoContractId || undefined)
        : undefined;

      const invoice = await createInvoiceMutation.mutateAsync({
        customer: mongoOrgId,
        sales_manager: salesManager,
        project_name: projectName.trim(),
        contract: resolvedContract,
        contractual_manager: contractualManager || undefined,
        language,
        date: invoiceDate || undefined,
        payment_terms: paymentTerms,
        tax_category: taxCategory,
        currency,
        quote_number: quoteNumber.trim() || undefined,
        po_number: poNumber.trim() || undefined,
        project_number: projectNumber.trim() || undefined,
        description: description.trim() || undefined,
        hubspot_link: hubspotLink.trim() || undefined,
        gdrive_link: gdriveLink.trim() || undefined,
      });

      onOpenChange(false);
      resetForm();
      onCreated?.(invoice);
    } catch {
      // Error is handled by the mutation hook (toast)
    }
  };

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={handleOpenChange}
      size="2xl"
      title="New Invoice"
      description="Create a new invoice. You can add SKUs and additional details after creation."
      onSubmit={handleSubmit}
      isSubmitting={createInvoiceMutation.isPending}
      saveDisabled={!isValid}
      saveLabel="New Invoice"
    >
      <div className="space-y-6">
          {/* Section 1: Account & Contract */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Account & Contract</h3>
            <Separator />
            {!customer && (
              <p className="text-xs text-muted-foreground">
                Pick an account to enable subscription, quote and contractual manager.
              </p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="customer">Account *</Label>
                <SearchableSelect
                  value={customer || null}
                  onValueChange={(v) => setCustomer(v || '')}
                  options={activeCustomers}
                  placeholder="Select account..."
                  emptyText="No accounts found."
                />
                <p className="text-xs text-muted-foreground">Active accounts only</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contract">Subscription</Label>
                <Select
                  value={contract}
                  onValueChange={setContract}
                  disabled={!customer || contractsQuery.isLoading}
                >
                  <SelectTrigger id="contract">
                    <SelectValue
                      placeholder={
                        !customer
                          ? '—'
                          : contractsQuery.isLoading
                            ? 'Loading...'
                            : 'Select subscription...'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {contracts.map((c) => (
                      <SelectItem key={c._id} value={c._id}>
                        {getContractDisplay(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quoteNumber">Quote</Label>
                <Select
                  value={quoteNumber}
                  onValueChange={setQuoteNumber}
                  disabled={!customer || quotesQuery.isLoading}
                >
                  <SelectTrigger id="quoteNumber">
                    <SelectValue
                      placeholder={
                        !customer
                          ? '—'
                          : quotesQuery.isLoading
                            ? 'Loading...'
                            : 'Select quote...'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {quotes.map((q) => (
                      <SelectItem key={q.id} value={q.quoteNumber}>
                        {q.quoteNumber}{q.projectName ? ` — ${q.projectName}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="salesManager">Account Manager *</Label>
                <Select value={salesManager} onValueChange={setSalesManager}>
                  <SelectTrigger id="salesManager">
                    <SelectValue placeholder="Select account manager..." />
                  </SelectTrigger>
                  <SelectContent>
                    {salesManagers
                      .filter((m) => !m.isFormer || m.mongoUserId === salesManager || m.id === salesManager)
                      .map((m) => (
                        <SelectItem key={m.id} value={m.mongoUserId || m.id}>
                          {m.name}{m.isFormer ? ' (former)' : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contractualManager">Contractual Manager</Label>
                <Select
                  value={contractualManager}
                  onValueChange={setContractualManager}
                  disabled={!customer || orgUsersQuery.isLoading}
                >
                  <SelectTrigger id="contractualManager">
                    <SelectValue
                      placeholder={
                        !customer
                          ? '—'
                          : orgUsersQuery.isLoading
                            ? 'Loading...'
                            : 'Select manager...'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {orgUsers.map((u) => (
                      <SelectItem key={u._id} value={u._id}>
                        {getUserName(u)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">Language</Label>
                <Select value={language} onValueChange={(v) => setLanguage(v as Language)}>
                  <SelectTrigger id="language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section 2: Invoice Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Invoice Settings</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="invoiceDate">Invoice Date</Label>
                <Input
                  id="invoiceDate"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="paymentTerms">Payment Terms</Label>
                <Select
                  value={paymentTerms}
                  onValueChange={(v) => setPaymentTerms(v as PaymentTerms)}
                >
                  <SelectTrigger id="paymentTerms">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on-order">On Order</SelectItem>
                    <SelectItem value="net-0">Net 0</SelectItem>
                    <SelectItem value="net-30">Net 30</SelectItem>
                    <SelectItem value="net-45">Net 45</SelectItem>
                    <SelectItem value="net-60">Net 60</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="taxCategory">Tax Category</Label>
                <Select
                  value={taxCategory}
                  onValueChange={(v) => setTaxCategory(v as TaxCategory)}
                >
                  <SelectTrigger id="taxCategory">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TAX_CATEGORY_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select value={currency} onValueChange={(v) => setCurrency(v as Currency)}>
                  <SelectTrigger id="currency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CAD">CAD</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Section 3: Project Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Project Info</h3>
            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label htmlFor="projectName">Invoice Name *</Label>
                <Input
                  id="projectName"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="projectNumber">Project</Label>
                <Select
                  value={projectNumber}
                  onValueChange={setProjectNumber}
                  disabled={!customer || projectsQuery.isLoading}
                >
                  <SelectTrigger id="projectNumber">
                    <SelectValue
                      placeholder={
                        !customer
                          ? '—'
                          : projectsQuery.isLoading
                            ? 'Loading...'
                            : 'Select project...'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.projectNumber}>
                        {p.projectNumber}{p.projectName ? ` — ${p.projectName}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="poNumber">PO Number</Label>
                <Input
                  id="poNumber"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="e.g. PO-12345"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Description & Links */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Description & Links</h3>
            <Separator />

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Invoice description or notes..."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hubspotLink">HubSpot Link</Label>
                  <Input
                    id="hubspotLink"
                    value={hubspotLink}
                    onChange={(e) => setHubspotLink(e.target.value)}
                    placeholder="https://app.hubspot.com/..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gdriveLink">Google Drive Link</Label>
                  <Input
                    id="gdriveLink"
                    value={gdriveLink}
                    onChange={(e) => setGdriveLink(e.target.value)}
                    placeholder="https://drive.google.com/..."
                  />
                </div>
              </div>
            </div>
          </div>

      </div>
    </EntityFormDialog>
  );
}
